"use client"

import React from "react"

import Link from "next/link"

import { useParams, useRouter } from "next/navigation"

import { Icon } from "@/components/formatters/MXPIcon"

import isExternalLink from "../../utils/isExternalLink"

import { StoreBadge, detectStore } from "../StoreBadge/StoreBadge"

import { localizeUrl } from "@/lib/url/localizeUrl"

import {

  MXP_DESTINATION_ATTR,

  navigateHashLinkFromClick,

} from "@/lib/scrollToAnchor"



export interface SpecialButton {

  primary?: boolean /* primary vs secondary */

  size?: "small" | "medium" | "large"

  label: string

  destination: string

  iconColor?: string

  isDarkTheme?: boolean

  utmParams?: Record<string, string>

}



/**

 * Primary UI component for user interaction

 */

export const SpecialButton = ({

  primary = true,

  size = "medium",

  label,

  destination,

  utmParams,

}: SpecialButton) => {

  const params = useParams()

  const router = useRouter()

  const lang = (params?.lang as string) || "en"

  const store = detectStore(destination)



  const isSecondary = !primary

  let iconSize = 12

  if (size == "small") {

    iconSize = 12

  } else if (size == "medium") {

    iconSize = 18

  } else if (size == "large") {

    iconSize = 20

  }



  const generateUrlWithUTM = (

    baseUrl: string,

    utmParams?: Record<string, string>,

  ) => {

    if (!baseUrl) return ""

    try {

      const url = new URL(baseUrl, window.location.origin)

      if (utmParams) {

        Object.entries(utmParams).forEach(([key, value]) => {

          url.searchParams.set(key, value)

        })

      }

      // Preserve absolute URLs that point to a different origin (e.g. sketchup.trimble.com)
      if (url.origin !== window.location.origin) {

        return url.href

      }

      return `${url.pathname}${url.search}${url.hash}`

    } catch {

      return baseUrl

    }

  }



  // const iconChosen = (size == 'large') ? 't2-icon--open-caret-right-dark-24px' : 't2-icon--open-caret-right-dark-8px';

  const iconChosen = size == "large" ? "t2u-chevron-right" : "t2u-chevron-right"



  const finalDestination = localizeUrl(

    generateUrlWithUTM(destination, utmParams),

    lang,

  )



  const handleClickCapture = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!finalDestination.includes("#")) return
    navigateHashLinkFromClick(event, finalDestination, href =>
      router.push(href, { scroll: false }),
    )
  }



  const wrapperClasses = [

    "group inline-flex relative items-center gap-[18px]",

    "text-gray-m9 text-sm font-medium leading-7 no-underline",

    "hover:text-gray-m9 hover:no-underline",

  ].join(" ")



  const buttonClasses = [

    "t2-spbutton__button",

    "flex items-center justify-center border-0 rounded-full",

    "w-[22px] h-[22px]",

    "transition-[background-color,border-color,box-shadow] duration-150 ease-in-out",

    isSecondary

      ? "text-white bg-gray-m9"

      : "bg-yellow-m10 group-hover:bg-yellow-m10",

    size !== "small" ? "min-w-12 min-h-12" : "",

  ].join(" ")



  const fieldClasses = [

    "t2-spbutton__field",

    "border-0 flex items-center justify-center text-end leading-5",

    "group-hover:underline",

    "text-gray-m9",

    isSecondary ? "t2-dark:text-white" : "t2-dark:text-yellow-m10",

  ].join(" ")



  if (isExternalLink(destination) === false && !store) {

    return (

      <Link

        className={wrapperClasses}

        href={finalDestination}

        scroll={false}

        tabIndex={0}

        {...{ [MXP_DESTINATION_ATTR]: finalDestination }}

        onClickCapture={handleClickCapture}

      >

        <div className={fieldClasses}>{label}</div>

        <div className={buttonClasses}>

          <Icon type={iconChosen} size={iconSize} fillColor="currentColor" />

        </div>

      </Link>

    )

  } else {

    return (

      <a

        className={wrapperClasses}

        href={finalDestination}

        tabIndex={0}

        target={isExternalLink(destination) ? "_blank" : "_self"}

        rel={isExternalLink(destination) ? "noopener noreferrer" : undefined}

        {...{ [MXP_DESTINATION_ATTR]: finalDestination }}

        onClickCapture={handleClickCapture}

      >

        {store ? (

          <StoreBadge store={store} />

        ) : (

          <>

            <div className={fieldClasses}>{label}</div>

            <div className={buttonClasses}>

              <Icon

                type={iconChosen}

                size={iconSize}

                fillColor="currentColor"

              />

            </div>

          </>

        )}

      </a>

    )

  }

}


